import { nsToDateLabel } from "@/shared/time/time";

import { type AxisDomain, padAxisToDomain } from "./axisDomain";
import { fillMissingBuckets } from "./fillMissingBuckets";
// Type-only: erased at compile time, so this pure module never pulls Chart.js
// (which `MetricChartView` registers at import time) into a Node-env test.
import type { PlotDatum } from "./MetricChartView";

/**
 * The slice of an aggregate record this module reads: one bucket's bounds and
 * value, plus the group the backend attributed it to.
 *
 * `group_key` is absent on every ungrouped response (and on every response
 * predating the feature); `null` means the row HAD the group field and it had
 * no value — a series in its own right, not a row to drop.
 */
export interface GroupableBucket {
  start_time: bigint;
  end_time: bigint;
  value: number;
  group_key?: string | null;
}

/** One plotted line: the group it stands for, its legend label, and its
 *  points — one per bucket of the shared category axis. */
export interface GroupedSeries {
  /** The wire value the drill-down filters on. `null` for the "no value"
   *  group, and for the single series of an ungrouped chart. */
  group_key: string | null;
  /** What the legend shows for this series. Empty on the single series of an
   *  UNGROUPED chart, which has no group to name — the view labels that one
   *  with the metric's own name, as it always has. */
  label: string;
  points: PlotDatum[];
}

/**
 * The legend label for one group key.
 *
 * A present key IS its own label — the backend casts every group key to text
 * precisely so the legend has one to show. A null key means the field was
 * unset on those rows; naming the FIELD's absence ("No device") reads better
 * than the generic form when the field is the device id, which is the one
 * group-by whose absence has an obvious noun.
 */
export function groupSeriesLabel(
  key: string | null,
  groupBy: string | undefined,
): string {
  if (key !== null) {
    return key;
  }
  return groupBy === "device.device_id" ? "No device" : "No value";
}

/** A bucket of the shared axis: real bounds, no value of its own. */
interface AxisBucket {
  start_time: bigint;
  end_time: bigint;
}

/**
 * The category axis every series is plotted against: the union of the bucket
 * bounds any group reported, gap-filled so a period nothing reported still
 * occupies a slot.
 *
 * The union, not any one group's buckets: a device that reported for half the
 * window would otherwise have half as many points as its neighbours, and
 * Chart.js — which positions category points by INDEX — would draw its last
 * point in the middle of the chart under someone else's date.
 */
function unionAxis(
  records: GroupableBucket[],
  domain?: AxisDomain,
): AxisBucket[] {
  const byStart = new Map<bigint, AxisBucket>();
  for (const record of records) {
    if (!byStart.has(record.start_time)) {
      byStart.set(record.start_time, {
        start_time: record.start_time,
        end_time: record.end_time,
      });
    }
  }
  // The domain carries the period the response was bucketed by, so the
  // gap-fill and the padding both step on the calendar boundaries `date_trunc`
  // puts the real buckets on. Without a domain there is no period to hand, and
  // the fill falls back to measuring each bucket's own width.
  const filled = fillMissingBuckets([...byStart.values()], domain?.period);
  return domain === undefined ? filled : padAxisToDomain(filled, domain);
}

/**
 * How many whole periods the response spans, first bucket to last — the same
 * gap-filled run the chart draws on its x-axis, so a card's caption and its
 * plot agree about how much time is on screen.
 *
 * Counted over the UNION of buckets, not over the records: a grouped response
 * carries one record per (bucket, group), so counting records would report a
 * three-device chart as spanning three times the days it does.
 *
 * Counted over the PADDED union when a `domain` is given, so the caption keeps
 * agreeing with the plot: an axis padded out to the dashboard's window shows
 * that window, and the caption says so.
 */
export function bucketSpan(
  records: GroupableBucket[],
  domain?: AxisDomain,
): number {
  return unionAxis(records, domain).length;
}

/**
 * Split an aggregate response into one gap-filled series per group.
 *
 * Every series carries a point for every bucket of {@link unionAxis}, valued
 * where that group reported and value-less where it did not, so all of them
 * share one category axis and one label list. With no grouping (or a response
 * whose records carry no `group_key`) the result is the single series the
 * chart drew before, gap-filled exactly as it was.
 *
 * Series are ordered alphabetically by key with the null group last: the order
 * has to be stable across refetches, or the legend and the colours would
 * reshuffle under the user whenever a group's values moved.
 *
 * `groupBy` is the field path the response was grouped by. It is used only to
 * label the null group (see {@link groupSeriesLabel}).
 *
 * `domain` is the span every card on the dashboard draws (see
 * {@link resolveAxisDomain}). Passing it pads the shared axis out to those
 * bounds, so two cards whose metrics stop reporting in different weeks still
 * agree about what is on screen. Omitting it keeps the axis bounded by the
 * response's own data, which is what every caller did before the dashboard had
 * a domain to hand down.
 */
export function toGroupedSeries(
  records: GroupableBucket[],
  groupBy: string | undefined,
  domain?: AxisDomain,
): GroupedSeries[] {
  if (records.length === 0) {
    return [];
  }

  const axis = unionAxis(records, domain);

  const valuesByGroup = new Map<string | null, Map<bigint, number>>();
  for (const record of records) {
    const key = record.group_key ?? null;
    let byStart = valuesByGroup.get(key);
    if (byStart === undefined) {
      byStart = new Map<bigint, number>();
      valuesByGroup.set(key, byStart);
    }
    byStart.set(record.start_time, record.value);
  }

  // Null sorts last whatever the other keys are; the rest compare as text,
  // which is all a group key ever is on the wire.
  const keys = [...valuesByGroup.keys()].sort((a, b) => {
    if (a === null) {
      return b === null ? 0 : 1;
    }
    if (b === null) {
      return -1;
    }
    return a.localeCompare(b, "en");
  });

  return keys.map((key) => {
    const values = valuesByGroup.get(key);
    // An ungrouped response has no group to name, so its points carry no
    // group at all: the view falls back to the metric name for the legend,
    // and the drill-down has no group predicate to add.
    const group =
      groupBy === undefined
        ? {}
        : { group_key: key, group_label: groupSeriesLabel(key, groupBy) };
    return {
      group_key: key,
      label: groupBy === undefined ? "" : groupSeriesLabel(key, groupBy),
      points: axis.map((slot) => ({
        start_time: slot.start_time,
        end_time: slot.end_time,
        value: values?.get(slot.start_time),
        // Only the display strings need a (lossy, but date-resolution-
        // irrelevant) `number`; the bounds ride along as bigint.
        label: nsToDateLabel(Number(slot.start_time)),
        tooltipTitle: `${nsToDateLabel(Number(slot.start_time))} - ${nsToDateLabel(Number(slot.end_time))}`,
        ...group,
      })),
    };
  });
}
